import type { ClassValue } from 'clsx';

import { clsx } from 'clsx';
import { jwtDecode } from 'jwt-decode';
import { redirect } from 'next/navigation';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function encodedRedirect(type: 'error' | 'success', path: string, message: string): never {
  return redirect(`${path}?${type}=${encodeURIComponent(message)}`);
}

export function formatDate(date: Date | string | number, opts: Intl.DateTimeFormatOptions = {}) {
  return new Intl.DateTimeFormat('en-US', {
    month: opts.month ?? 'long',
    day: opts.day ?? 'numeric',
    year: opts.year ?? 'numeric',
    ...opts,
  }).format(new Date(date));
}

export function toSentenceCase(str: string) {
  return str
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase())
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @see https://github.com/radix-ui/primitives/blob/main/packages/core/primitive/src/primitive.tsx
 */
export function composeEventHandlers<E>(
  originalEventHandler?: (event: E) => void,
  ourEventHandler?: (event: E) => void,
  { checkForDefaultPrevented = true } = {}
) {
  return function handleEvent(event: E) {
    originalEventHandler?.(event);

    if (checkForDefaultPrevented === false || !(event as unknown as Event).defaultPrevented) {
      return ourEventHandler?.(event);
    }
  };
}

export function getUserClaims(accessToken: string): { [key: string]: string } {
  const jwt = jwtDecode(accessToken);

  return jwt as { [key: string]: string };
}

export function convertLocationNameToSlug(locationName: string): string {
  // Convertir a minúsculas
  let slug = locationName.toLowerCase();

  // Reemplazar caracteres no alfanuméricos por guiones
  slug = slug.replace(/[^a-z0-9]+/g, '-');
  // Eliminar guiones al inicio y al final
  slug = slug.replace(/^-+|-+$/g, '');

  return slug;
}

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.buffer;
}

export const DisplayInitialColors = {
  background: '0 0% 100%',
  foreground: '240 10% 3.9%',
  primary: '240 5.9% 10%',
  'primary-foreground': '0 0% 98%',
  accent: '240 4.8% 95.9%',
  'accent-foreground': '240 5.9% 10%',
  border: '240 5.9% 90%',
  ring: '240 5.9% 10%',
} as const;

export const DisplayColorsTranslations: Record<keyof typeof DisplayInitialColors, string> = {
  background: 'Fondo',
  foreground: 'Texto',
  primary: 'Primario',
  'primary-foreground': 'Texto primario',
  accent: 'Accesorio',
  'accent-foreground': 'Texto de accesorio',
  border: 'Borde',
  ring: 'Anillo',
} as const;

export function formatCompactCurrency(value: number): string {
  const formatted = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
  return `$ ${formatted}`;
}

const formatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Argentina/Buenos_Aires',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function getArgParts(dateIso: string) {
  const parts = formatter.formatToParts(new Date(dateIso));
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value);

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
}
